import React, { useEffect, useRef, useState } from 'react';
import { Form, Row, Col, Alert, Button, Card, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { EMAIL_PATTERN } from '@/shared/utils/email';
import CourseTypeahead from '@/components/common/CourseTypeahead';
import applicationService from '@/shared/services/applicationService';
import courseService from '@/shared/services/courseService';
import {
    OTHER_PART_OF_TERM,
    PART_OF_TERM_MAX_LENGTH,
    partOfTermDates,
    partOfTermOptionLabel,
} from '@/shared/utils/partsOfTerm';
import { isOwnDraftMatch } from './draftApplication';
import CrnHolderNotice from './CrnHolderNotice';
import { courseChangeWarning, losesAnything } from './courseChange';
import {
    MAX_MEETINGS,
    MEETING_TYPE_OPTIONS,
    emptyMeeting,
    getMeetingErrors,
} from './meetings';

// Exported for ApplicationDetailPage.js, whose Eligibility & Course step
// applies the same format check to a returned request's CRN.
export const CRN_PATTERN = /^\d{5}$/;
// A 2-4 letter subject and a 3-4 digit number with an optional trailing
// letter (e.g. BIO 398A) — matches what POST /api/course/unlisted itself
// validates (course.controller.js's own SUBJECT_PATTERN/
// COURSE_NUMBER_PATTERN), so a malformed value is caught here instead of
// round-tripping to the server first. Keep these in sync with that file.
const SUBJECT_PATTERN = /^[A-Za-z]{2,4}$/;
const COURSE_NUMBER_PATTERN = /^\d{3,4}[A-Za-z]?$/;
// Matches CourseTypeahead's own debounce reasoning — a live per-keystroke
// check would fire on every digit of "12345", not just once it's complete.
const CRN_CHECK_DEBOUNCE_MS = 300;

// Same options as Course's modality enum
export const MODALITY_OPTIONS = [
    { value: 'in_person', label: 'In Person' },
    { value: 'hybrid', label: 'Hybrid' },
    { value: 'online', label: 'Online' },
    { value: 'other', label: 'Other' },
];

// MSU Denver's standard Fall/Spring day patterns (Registrar time block schedule)
export const DAY_OPTIONS = [
    { value: 'MWF', label: 'Monday/Wednesday/Friday' },
    { value: 'MW', label: 'Monday/Wednesday' },
    { value: 'TR', label: 'Tuesday/Thursday' },
    { value: 'F', label: 'Friday' },
    { value: 'Other', label: 'Other' },
];

// MSU Denver's standard class blocks, grouped by length. The 75 and 50
// minute blocks are the Registrar's 3-credit-hour Fall/Spring time blocks
// (TR/MW and MWF); the 110 minute blocks stay for sections that use them.
// Source: https://www.msudenver.edu/wp-content/uploads/2021/06/Class-Time-Block.pdf
// Every value must stay in "H:MM AM - H:MM PM" form, which
// shared/utils/positionFilters.js and positionKeyFacts.js parse.
export const TIME_OPTION_GROUPS = [
    {
        label: '75 minutes (Tuesday/Thursday or Monday/Wednesday)',
        times: [
            '6:30 AM - 7:45 AM',
            '8:00 AM - 9:15 AM',
            '9:30 AM - 10:45 AM',
            '11:00 AM - 12:15 PM',
            '12:30 PM - 1:45 PM',
            '2:00 PM - 3:15 PM',
            '3:30 PM - 4:45 PM',
            '5:30 PM - 6:45 PM',
            '7:00 PM - 8:15 PM',
            '8:30 PM - 9:45 PM',
        ],
    },
    {
        label: '50 minutes (Monday/Wednesday/Friday)',
        times: [
            '6:00 AM - 6:50 AM',
            '7:00 AM - 7:50 AM',
            '8:00 AM - 8:50 AM',
            '10:00 AM - 10:50 AM',
            '11:00 AM - 11:50 AM',
            '1:00 PM - 1:50 PM',
            '2:00 PM - 2:50 PM',
            '4:00 PM - 4:50 PM',
            '5:00 PM - 5:50 PM',
            '7:00 PM - 7:50 PM',
            '8:00 PM - 8:50 PM',
        ],
    },
    {
        label: '110 minutes',
        times: [
            '8:00 AM - 9:50 AM',
            '10:00 AM - 11:50 AM',
            '12:00 PM - 1:50 PM',
            '2:00 PM - 3:50 PM',
            '4:00 PM - 5:50 PM',
            '6:00 PM - 7:50 PM',
            '8:00 PM - 9:50 PM',
        ],
    },
];

// Narrower sibling of getEligibilityCourseErrors below, covering only the
// "Other" free-text fields (#403, and each meeting's since #609) — same
// rule the server enforces at submit time in
// hasIncompleteCourseConfirmationOtherField (can't share the
// function across the client/server boundary). ApplicationDetailPage.js uses
// it as is to tell whether a returned request is missing one of those, and
// its Eligibility & Course step passes meetingOptions so every meeting also
// needs its type, days, time and room, as in the create wizard. Not called
// from getEligibilityCourseErrors below: that function's own checks are
// ordered to match Section 1's DOM (ticket #384's error-ordering fix), and
// splicing this in would fight that. Both get their meeting checks from
// getMeetingErrors, so those can't drift apart. idPrefix matches whichever
// page's own DOM ids the caller built.
export function getCourseConfirmationOtherFieldErrors(
    courseConfirmation,
    idPrefix = 'ca',
    meetingOptions = { required: false }
) {
    const errors = [];
    if (
        courseConfirmation.modality === 'other' &&
        !courseConfirmation.otherModality?.trim()
    ) {
        errors.push({
            id: `${idPrefix}-other-modality`,
            label: 'Modality description',
        });
    }
    errors.push(
        ...getMeetingErrors(
            courseConfirmation.meetings,
            idPrefix,
            meetingOptions
        )
    );
    return errors;
}

// Single source of truth for "is Eligibility & Course complete" and, on a
// blocked advance, exactly which fields are missing — each entry's `id`
// matches that field's DOM id (Bootstrap's controlId, or an explicit id on
// conditionally-rendered fields), so CreateApplication.js can both list the
// field by `label` and move focus to it. Only CreateApplication.js uses
// this step (ApplicationDetailPage.js's edit/resubmit flow has its own
// Eligibility & Course step with different rules), so there's no idPrefix to
// parameterize here.
export function getEligibilityCourseErrors(
    courseConfirmation,
    isSubmittedOnBehalf,
    // Whether Next should stay blocked on the CRN field for a reason other
    // than its format: either the live CRN check (below, in the component)
    // found a submitted application claiming this CRN+term, the applicant's
    // own or someone else's (only one submitted application per CRN per
    // term; another of the applicant's own drafts claims nothing and does
    // not block), or that check is still in flight for the currently-typed
    // CRN, so "no conflict found (yet)" can't be trusted until it resolves.
    // This function can't run or await the check itself (it's a pure sync
    // function over form state), so the component reports both back up
    // through this single flag.
    crnBlocksAdvance = false
) {
    const errors = [];
    // First fields on the form (course display sits above them but isn't
    // itself validated here — a missing course is CreateApplication.js's
    // own "No course selected" guard, and the unlisted-course fields have
    // their own errors pushed by the caller before this function runs, see
    // CreateApplication.js), so enrollment is first in this list too.
    if (!courseConfirmation.enrollment) {
        errors.push({
            id: 'ca-enrollment',
            label: 'Maximum course enrollment',
        });
    }
    if (!CRN_PATTERN.test(courseConfirmation.crn ?? '') || crnBlocksAdvance) {
        errors.push({ id: 'ca-crn', label: 'CRN' });
    }
    if (!courseConfirmation.sectionNumber?.trim()) {
        errors.push({ id: 'ca-section-number', label: 'Section #' });
    }
    // Sits right under the CRN and section number in the DOM
    if (!courseConfirmation.partOfTermCode) {
        errors.push({ id: 'ca-part-of-term', label: 'Part of term' });
    } else if (
        courseConfirmation.partOfTermCode === OTHER_PART_OF_TERM &&
        !courseConfirmation.partOfTermName?.trim()
    ) {
        errors.push({
            id: 'ca-other-part-of-term',
            label: 'Part of term description',
        });
    }
    // Pushed here, not after the meeting fields below, to match where the
    // instructor email actually renders in the DOM (right after
    // the "instructor of record?" radios, before Course Modality) — same
    // reasoning as CreateApplicationSection5's LA error reordering: keeps
    // the first-error focus target and the "Missing: ..." summary reading
    // top-to-bottom instead of jumping around.
    // Name and email are optional (#455) — the person filing may not know
    // the instructor yet. Only flag an email that was typed but is invalid.
    const iorEmail = courseConfirmation.instructorOfRecordEmail?.trim() ?? '';
    if (isSubmittedOnBehalf && iorEmail && !EMAIL_PATTERN.test(iorEmail)) {
        errors.push({ id: 'ca-ior-email', label: 'Instructor Email' });
    }
    if (!courseConfirmation.modality) {
        errors.push({ id: 'ca-modality', label: 'Course Modality' });
    }
    if (
        courseConfirmation.modality === 'other' &&
        !courseConfirmation.otherModality?.trim()
    ) {
        errors.push({ id: 'ca-other-modality', label: 'Modality description' });
    }
    // Every meeting is checked the same way, in DOM order
    errors.push(...getMeetingErrors(courseConfirmation.meetings, 'ca'));
    return errors;
}

// An "Other" choice's required free-text answer. Also used by
// ApplicationDetailPage.js's Eligibility & Course step.
export function OtherTextField({
    id,
    label,
    placeholder,
    value,
    onChange,
    hasError,
    maxLength,
    refusal,
}) {
    const describedBy = [
        maxLength && `${id}-count`,
        (hasError(id) || refusal) && `${id}-error`,
    ]
        .filter(Boolean)
        .join(' ');
    return (
        <>
            <Form.Label htmlFor={id}>
                {label} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
                id={id}
                type="text"
                placeholder={placeholder}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                maxLength={maxLength}
                required
                isInvalid={hasError(id) || !!refusal}
                aria-invalid={hasError(id) || !!refusal}
                aria-describedby={describedBy || undefined}
            />
            {maxLength && (
                <Form.Text id={`${id}-count`}>
                    {(value || '').length} of {maxLength} characters
                </Form.Text>
            )}
            {refusal && !hasError(id) && (
                <Form.Control.Feedback
                    type="invalid"
                    id={`${id}-error`}
                    role="alert"
                    className="d-block"
                >
                    {refusal}
                </Form.Control.Feedback>
            )}
            {hasError(id) && (
                <Form.Control.Feedback
                    type="invalid"
                    id={`${id}-error`}
                    role="alert"
                >
                    This field is required.
                </Form.Control.Feedback>
            )}
        </>
    );
}

// A required select with its "Other" free text, e.g. modality or a
// meeting's days
function SelectWithOther({
    id,
    label,
    placeholder,
    options,
    otherValue,
    value,
    onSelect,
    otherId,
    otherText,
    otherLabel,
    otherPlaceholder,
    onOtherChange,
    otherMaxLength,
    refusal,
    hasError,
    hint,
}) {
    // The "Other" field sits outside the select's Form.Group, whose
    // controlId would otherwise clash with that field's own id
    return (
        <>
            <Form.Group controlId={id}>
                <Form.Label>
                    {label} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                    value={value}
                    onChange={(e) => onSelect(e.target.value)}
                    required
                    isInvalid={
                        hasError(id) || (!!refusal && value !== otherValue)
                    }
                    aria-invalid={
                        hasError(id) || (!!refusal && value !== otherValue)
                    }
                    aria-describedby={
                        [
                            hint && `${id}-hint`,
                            (hasError(id) ||
                                (refusal && value !== otherValue)) &&
                                `${id}-error`,
                        ]
                            .filter(Boolean)
                            .join(' ') || undefined
                    }
                >
                    <option value="">{placeholder}</option>
                    {options.map((opt) =>
                        opt.options ? (
                            <optgroup key={opt.label} label={opt.label}>
                                {opt.options.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </optgroup>
                        ) : (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        )
                    )}
                </Form.Select>
                {hint && <Form.Text id={`${id}-hint`}>{hint}</Form.Text>}
                {refusal && value !== otherValue && !hasError(id) && (
                    <Form.Control.Feedback
                        type="invalid"
                        id={`${id}-error`}
                        role="alert"
                        className="d-block"
                    >
                        {refusal}
                    </Form.Control.Feedback>
                )}
                {hasError(id) && (
                    <Form.Control.Feedback
                        type="invalid"
                        id={`${id}-error`}
                        role="alert"
                    >
                        This field is required.
                    </Form.Control.Feedback>
                )}
            </Form.Group>
            {value === otherValue && (
                <div className="mt-2">
                    <OtherTextField
                        id={otherId}
                        label={otherLabel}
                        placeholder={otherPlaceholder}
                        value={otherText}
                        onChange={onOtherChange}
                        hasError={hasError}
                        maxLength={otherMaxLength}
                        refusal={refusal}
                    />
                </div>
            )}
        </>
    );
}

// The section's part of term: the term's sessions from the Registrar, with
// the full semester chosen until the faculty member picks another, and Other
// with free text for anything not listed.
function PartOfTermField({ value, onChange, partsOfTerm, refusal, hasError }) {
    const parts = partsOfTerm || [];
    const code = value.partOfTermCode || '';
    // A saved choice the term's list no longer holds still shows as chosen
    const keptChoice =
        code &&
        code !== OTHER_PART_OF_TERM &&
        !parts.some((part) => part.code === code)
            ? [{ code, name: value.partOfTermName || code }]
            : [];
    const options = [
        ...[...parts, ...keptChoice].map((part) => ({
            value: part.code,
            label: partOfTermOptionLabel(part),
        })),
        { value: OTHER_PART_OF_TERM, label: 'Other' },
    ];
    const nameOf = (chosen) =>
        [...parts, ...keptChoice].find((part) => part.code === chosen)?.name ||
        '';
    // A phone's closed select cuts the dates off, so the hint repeats them
    const chosen = parts.find((part) => part.code === code);
    const chosenDates = partOfTermDates(chosen);
    const chosenName = chosen?.name;
    return (
        <SelectWithOther
            id="ca-part-of-term"
            label="Part of term"
            placeholder={partsOfTerm ? 'Select part of term...' : 'Loading...'}
            options={options}
            otherValue={OTHER_PART_OF_TERM}
            value={code}
            onSelect={(chosen) =>
                onChange({
                    partOfTermCode: chosen,
                    // Other starts blank for the faculty member's own words
                    partOfTermName:
                        chosen === OTHER_PART_OF_TERM ? '' : nameOf(chosen),
                })
            }
            otherId="ca-other-part-of-term"
            otherText={value.partOfTermName}
            otherLabel="Describe the part of term"
            otherPlaceholder="e.g. Weekend intensive, Jan 23 to Feb 28"
            onOtherChange={(partOfTermName) => onChange({ partOfTermName })}
            otherMaxLength={PART_OF_TERM_MAX_LENGTH}
            // The server's refusal stays only while the answer is the one
            // it refused
            refusal={
                refusal &&
                refusal.code === value.partOfTermCode &&
                refusal.name === value.partOfTermName
                    ? refusal.message
                    : undefined
            }
            hasError={hasError}
            hint={
                <>
                    Choose Full Semester Session unless this section runs for
                    only part of the term.
                    {chosenDates && (
                        <span className="d-block">
                            {chosenName}: {chosenDates}
                        </span>
                    )}
                </>
            }
        />
    );
}

const TIME_SELECT_OPTIONS = [
    ...TIME_OPTION_GROUPS.map(({ label, times }) => ({
        label,
        options: times.map((t) => ({ value: t, label: t })),
    })),
    { value: 'Other', label: 'Other' },
];

// Every meeting time for the section (#609), e.g. a lecture and a lab
// that meet at different times. The first can't be removed, since a
// section meets at least once. In a dialog, narrower than the page, the
// fields go two to a row so a long choice isn't cut off.
export function MeetingFields({
    meetings,
    onChange,
    hasError,
    idPrefix = 'ca',
    inDialog = false,
}) {
    const wideColumns = inDialog ? 6 : 3;
    // Where focus goes after adding or removing a meeting, so it isn't
    // lost on the removed row
    const [focusId, setFocusId] = useState(null);
    useEffect(() => {
        if (focusId) {
            document.getElementById(focusId)?.focus();
            setFocusId(null);
        }
    }, [focusId]);

    const update = (index, patch) =>
        onChange(
            meetings.map((m, i) => (i === index ? { ...m, ...patch } : m))
        );
    const add = () => {
        onChange([...meetings, emptyMeeting()]);
        setFocusId(`${idPrefix}-meeting-${meetings.length}-type`);
    };
    const remove = (index) => {
        onChange(meetings.filter((_, i) => i !== index));
        setFocusId(`${idPrefix}-add-meeting`);
    };

    return (
        <div className="mb-3">
            <h3 className="h6 mb-1">Meeting times</h3>
            <p className="text-muted small mb-2">
                Add each time the section meets, such as a lecture and a lab
                that meet at different times.
            </p>
            {meetings.map((meeting, i) => {
                const id = `${idPrefix}-meeting-${i}`;
                return (
                    <fieldset key={i} className="border rounded p-3 mb-2">
                        <legend className="fs-6 fw-semibold mb-2">
                            Meeting {i + 1}
                        </legend>
                        <Row className="g-3">
                            <Col md={6} lg={wideColumns}>
                                <SelectWithOther
                                    id={`${id}-type`}
                                    otherId={`${id}-other-type`}
                                    label="Meeting type"
                                    placeholder="Select type..."
                                    options={MEETING_TYPE_OPTIONS}
                                    otherValue="other"
                                    value={meeting.type}
                                    onSelect={(type) =>
                                        update(i, {
                                            type,
                                            ...(type !== 'other' && {
                                                otherType: '',
                                            }),
                                        })
                                    }
                                    otherText={meeting.otherType}
                                    otherLabel="Describe the other meeting type"
                                    otherPlaceholder="e.g. studio"
                                    onOtherChange={(otherType) =>
                                        update(i, { otherType })
                                    }
                                    hasError={hasError}
                                />
                            </Col>
                            <Col md={6} lg={wideColumns}>
                                <SelectWithOther
                                    id={`${id}-days`}
                                    otherId={`${id}-other-days`}
                                    label="Days"
                                    placeholder="Select days..."
                                    options={DAY_OPTIONS}
                                    otherValue="Other"
                                    value={meeting.days}
                                    onSelect={(days) =>
                                        update(i, {
                                            days,
                                            ...(days !== 'Other' && {
                                                otherDays: '',
                                            }),
                                        })
                                    }
                                    otherText={meeting.otherDays}
                                    otherLabel="Describe the other day pattern"
                                    otherPlaceholder="e.g. every other Tuesday"
                                    onOtherChange={(otherDays) =>
                                        update(i, { otherDays })
                                    }
                                    hasError={hasError}
                                />
                            </Col>
                            <Col md={6} lg={wideColumns}>
                                <SelectWithOther
                                    id={`${id}-time`}
                                    otherId={`${id}-other-time`}
                                    label="Time"
                                    placeholder="Select time..."
                                    options={TIME_SELECT_OPTIONS}
                                    otherValue="Other"
                                    value={meeting.time}
                                    onSelect={(time) =>
                                        update(i, {
                                            time,
                                            ...(time !== 'Other' && {
                                                otherTime: '',
                                            }),
                                        })
                                    }
                                    otherText={meeting.otherTime}
                                    otherLabel="Describe the other time"
                                    otherPlaceholder="e.g. 7:00 AM - 8:50 AM"
                                    onOtherChange={(otherTime) =>
                                        update(i, { otherTime })
                                    }
                                    hasError={hasError}
                                />
                            </Col>
                            <Col md={6} lg={wideColumns}>
                                <Form.Group controlId={`${id}-room`}>
                                    <Form.Label>
                                        Room{' '}
                                        <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="e.g., King Center 320"
                                        value={meeting.room}
                                        onChange={(e) =>
                                            update(i, { room: e.target.value })
                                        }
                                        required
                                        isInvalid={hasError(`${id}-room`)}
                                        aria-invalid={hasError(`${id}-room`)}
                                        aria-describedby={
                                            hasError(`${id}-room`)
                                                ? `${id}-room-help ${id}-room-error`
                                                : `${id}-room-help`
                                        }
                                    />
                                    {/* A room is often assigned late, so
                                        say how to answer without one */}
                                    <Form.Text id={`${id}-room-help`} muted>
                                        Not assigned yet? Enter TBD. Online?
                                        Enter Online.
                                    </Form.Text>
                                    {hasError(`${id}-room`) && (
                                        <Form.Control.Feedback
                                            type="invalid"
                                            id={`${id}-room-error`}
                                            role="alert"
                                        >
                                            This field is required.
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>
                        {i > 0 && (
                            <div className="text-end mt-2">
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => remove(i)}
                                    aria-label={`Remove meeting ${i + 1}`}
                                >
                                    Remove
                                </Button>
                            </div>
                        )}
                    </fieldset>
                );
            })}
            {meetings.length < MAX_MEETINGS ? (
                <Button
                    id={`${idPrefix}-add-meeting`}
                    variant="outline-primary"
                    size="sm"
                    onClick={add}
                >
                    + Add another meeting time
                </Button>
            ) : (
                <p className="text-muted small mb-0">
                    {MAX_MEETINGS} meeting times is the most one request can
                    list.
                </p>
            )}
        </div>
    );
}

// Section 1: Eligibility and Course
// The pending CRN check's timer. Kept outside the component and cleared
// on the next CRN change rather than in a cleanup, so leaving the section
// (#463) lets a pending check finish instead of leaving the parent stuck
// on "checking", and coming back before it fires doesn't send a second
// request for the same CRN.
let pendingCrnTimer = null;

function CreateApplicationSection1({
    value,
    onChange,
    course,
    onCourseChange,
    courseChangeLoss,
    isDuplicate = false,
    refocusChangeCourseRef = undefined,
    termCode,
    excludeApplicationId,
    isSubmittedOnBehalf,
    onIsSubmittedOnBehalfChange,
    crnCheckResult,
    rankedProgramKeys,
    onCrnCheckResult,
    onCrnCheckingChange,
    onCoursePickerModeChange,
    partsOfTerm,
    partOfTermRefusal,
    unlistedDefaults,
    unlistedForm: savedUnlistedForm,
    onUnlistedFormChange,
    errorIds = new Set(),
}) {
    const hasError = (id) => errorIds.has(id);
    const navigate = useNavigate();
    // Three states for the course picker: showing the current course,
    // searching for a different one, or typing in one the catalog doesn't
    // have. 'search' and 'unlisted' both start from "Change course", and
    // the search view offers a way into 'unlisted' too — the standalone
    // entry point (#407) already runs the typeahead before step 1 even
    // mounts and has its own "not listed" link, so this only needs to
    // cover the "change my mind" case, not picking a course from empty.
    const [coursePickerMode, setCoursePickerModeState] = useState(
        course ? 'display' : 'unlisted'
    );
    const onCoursePickerModeChangeRef = useRef(onCoursePickerModeChange);
    onCoursePickerModeChangeRef.current = onCoursePickerModeChange;
    // Lifted to CreateApplication.js too — while the user is mid "Change
    // course" (search or unlisted, not yet confirmed), the parent's `course`
    // is still the OLD one, so eligibilityCourseErrors would otherwise count
    // the section complete using stale course data paired with whatever the
    // user is still typing into the abandoned picker.
    const setCoursePickerMode = (mode) => {
        setCoursePickerModeState(mode);
        onCoursePickerModeChangeRef.current?.(mode);
    };
    // Leaving the picker, by a pick or by backing out, lands on the control
    // that started the change. Loading a different course remounts this
    // section, so the parent carries that case in refocusChangeCourseRef for
    // the new mount to act on.
    const changeCourseRef = useRef(null);
    const returnFocusRef = useRef(false);
    const returnToCourse = () => {
        returnFocusRef.current = true;
        setCoursePickerMode('display');
    };
    useEffect(() => {
        if (coursePickerMode === 'display' && returnFocusRef.current) {
            returnFocusRef.current = false;
            changeCourseRef.current?.focus();
        }
    }, [coursePickerMode]);
    useEffect(() => {
        if (refocusChangeCourseRef?.current && changeCourseRef.current) {
            refocusChangeCourseRef.current = false;
            changeCourseRef.current.focus();
        }
        // only on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // The unlisted form opened from a change starts at its first field. On
    // the standalone entry (no course yet) it is simply the page.
    useEffect(() => {
        if (coursePickerMode === 'unlisted' && course) {
            document.getElementById('ca-unlisted-subject')?.focus();
        }
        // only when the form opens
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coursePickerMode]);
    // Leaving the section (#463) drops a half-done course change, since
    // coming back remounts on the current course. Without this the parent
    // would keep reporting "Course" missing with nothing on screen to fix.
    useEffect(() => () => onCoursePickerModeChangeRef.current?.('display'), []);
    // A duplicate whose course isn't in this term's catalog starts from the
    // copied course's details (#457)
    // Kept by the parent so a half-typed course survives leaving the
    // section (#463). Null until someone types in it.
    const unlistedForm = savedUnlistedForm ?? {
        subject: unlistedDefaults?.subject || '',
        courseNumber: unlistedDefaults?.courseNumber || '',
        title: unlistedDefaults?.title || '',
    };
    const setUnlistedForm = (update) =>
        onUnlistedFormChange(update(unlistedForm));
    const [unlistedError, setUnlistedError] = useState(null);
    const [unlistedSubmitting, setUnlistedSubmitting] = useState(false);

    const [crnChecking, setCrnChecking] = useState(false);
    const ownDraftMatch = isOwnDraftMatch(crnCheckResult);
    // Guards against a stale response landing after a faster, later one —
    // same race CourseTypeahead's latestQueryRef guards against.
    const latestCrnRef = useRef('');
    const onCrnCheckResultRef = useRef(onCrnCheckResult);
    onCrnCheckResultRef.current = onCrnCheckResult;
    const onCrnCheckingChangeRef = useRef(onCrnCheckingChange);
    onCrnCheckingChangeRef.current = onCrnCheckingChange;
    // Lifted to CreateApplication.js too — a resolved "no conflict" isn't
    // enough on its own to count the CRN done while a check for the
    // currently-typed CRN is still in flight, or a fast submit right after
    // the fifth digit could slip through before the debounce fires.
    // Both report the CRN they're about, so the parent can drop a late
    // answer from an earlier mount of this section (#463)
    const setChecking = (checking, crn) => {
        setCrnChecking(checking);
        onCrnCheckingChangeRef.current?.(checking, crn);
    };

    useEffect(() => {
        clearTimeout(pendingCrnTimer);
        const crn = value.crn ?? '';
        latestCrnRef.current = crn;
        if (!CRN_PATTERN.test(crn)) {
            setChecking(false, crn);
            onCrnCheckResultRef.current?.(null, crn);
            return undefined;
        }

        setChecking(true, crn);
        pendingCrnTimer = setTimeout(async () => {
            try {
                // The saved draft being edited isn't a conflict with itself
                const result = await applicationService.checkCrn(
                    termCode,
                    crn,
                    excludeApplicationId
                );
                if (latestCrnRef.current !== crn) return;
                onCrnCheckResultRef.current?.(result, crn);
            } catch (_err) {
                if (latestCrnRef.current !== crn) return;
                // A failed check is treated as "no conflict found" rather
                // than blocking Next on a network hiccup — the database's
                // partial unique index is still the real rule and will
                // refuse a genuine duplicate at submit either way.
                onCrnCheckResultRef.current?.(null, crn);
            } finally {
                if (latestCrnRef.current === crn) setChecking(false, crn);
            }
        }, CRN_CHECK_DEBOUNCE_MS);
        return undefined;
    }, [value.crn, termCode, excludeApplicationId]);

    const submitUnlistedCourse = async () => {
        const subject = unlistedForm.subject.trim().toUpperCase();
        const courseNumber = unlistedForm.courseNumber.trim().toUpperCase();
        const title = unlistedForm.title.trim();
        if (!SUBJECT_PATTERN.test(subject)) {
            setUnlistedError('Enter a 2-4 letter subject, like CHE.');
            return;
        }
        if (!COURSE_NUMBER_PATTERN.test(courseNumber)) {
            setUnlistedError(
                'Enter a 3-4 digit course number, like 1800 or 398A, with an optional letter.'
            );
            return;
        }
        if (!title) {
            setUnlistedError('Enter the course title.');
            return;
        }

        setUnlistedSubmitting(true);
        setUnlistedError(null);
        try {
            const created = await courseService.createUnlistedCourse({
                term: termCode,
                subject,
                courseNumber,
                title,
            });
            onCourseChange(created);
            returnToCourse();
        } catch (err) {
            setUnlistedError(
                err.response?.data?.message ||
                    'Could not add that course. Please try again.'
            );
        } finally {
            setUnlistedSubmitting(false);
        }
    };

    return (
        <div>
            <h2 className="h5 mb-3">Eligibility &amp; Course</h2>
            <Alert variant="info">
                Make one support request for each CRN you want peer support for.
            </Alert>

            <Card className="border-0 bg-light mb-3">
                <Card.Body>
                    {coursePickerMode === 'display' && course && (
                        <div className="d-flex align-items-center justify-content-between">
                            <div>
                                <span className="fw-semibold">
                                    {course.subject} {course.courseNumber}
                                </span>{' '}
                                <span className="text-muted">
                                    {course.title}
                                </span>
                            </div>
                            <Button
                                ref={changeCourseRef}
                                variant="link"
                                size="sm"
                                className="p-0"
                                onClick={() => setCoursePickerMode('search')}
                            >
                                Change course
                            </Button>
                        </div>
                    )}
                    {coursePickerMode !== 'display' &&
                        losesAnything(courseChangeLoss) && (
                            <Alert variant="warning" className="py-2 small">
                                {courseChangeWarning(
                                    courseChangeLoss,
                                    isDuplicate
                                )}
                            </Alert>
                        )}
                    {coursePickerMode === 'search' && (
                        <div>
                            <CourseTypeahead
                                termCode={termCode}
                                label="Which course do you teach?"
                                autoFocus
                                onSelect={(selected) => {
                                    onCourseChange(selected);
                                    returnToCourse();
                                }}
                            />
                            <div className="d-flex align-items-center gap-3 mt-2">
                                <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0"
                                    onClick={() =>
                                        setCoursePickerMode('unlisted')
                                    }
                                >
                                    My course isn&apos;t listed
                                </Button>
                                {course && (
                                    <Button
                                        variant="outline-dark"
                                        size="sm"
                                        onClick={returnToCourse}
                                    >
                                        Cancel
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                    {coursePickerMode === 'unlisted' && (
                        <div>
                            <p className="fw-semibold mb-2">
                                Tell us about your course
                            </p>
                            <p className="text-muted small mb-3">
                                For an omnibus or special-topics course, or
                                anything else the catalog doesn&apos;t have yet.
                            </p>
                            <Row className="g-2">
                                <Col md={3}>
                                    <Form.Group controlId="ca-unlisted-subject">
                                        <Form.Label>Subject</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="e.g., CHE"
                                            value={unlistedForm.subject}
                                            onChange={(e) =>
                                                setUnlistedForm((prev) => ({
                                                    ...prev,
                                                    subject: e.target.value,
                                                }))
                                            }
                                            isInvalid={hasError(
                                                'ca-unlisted-subject'
                                            )}
                                            aria-invalid={hasError(
                                                'ca-unlisted-subject'
                                            )}
                                            aria-describedby={
                                                hasError('ca-unlisted-subject')
                                                    ? 'ca-unlisted-subject-error'
                                                    : undefined
                                            }
                                        />
                                        {hasError('ca-unlisted-subject') && (
                                            <Form.Control.Feedback
                                                type="invalid"
                                                id="ca-unlisted-subject-error"
                                                role="alert"
                                            >
                                                Pick a course above, or fill
                                                this form in and use it.
                                            </Form.Control.Feedback>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group controlId="ca-unlisted-number">
                                        <Form.Label>Course number</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="e.g., 1800"
                                            value={unlistedForm.courseNumber}
                                            onChange={(e) =>
                                                setUnlistedForm((prev) => ({
                                                    ...prev,
                                                    courseNumber:
                                                        e.target.value,
                                                }))
                                            }
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group controlId="ca-unlisted-title">
                                        <Form.Label>Course title</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="e.g., Special Topics in Chemistry"
                                            value={unlistedForm.title}
                                            onChange={(e) =>
                                                setUnlistedForm((prev) => ({
                                                    ...prev,
                                                    title: e.target.value,
                                                }))
                                            }
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            {unlistedError && (
                                <Alert variant="danger" className="mt-2 mb-0">
                                    {unlistedError}
                                </Alert>
                            )}
                            <div className="mt-3 d-flex gap-2">
                                <Button
                                    variant="primary"
                                    size="sm"
                                    disabled={unlistedSubmitting}
                                    onClick={submitUnlistedCourse}
                                >
                                    {unlistedSubmitting
                                        ? 'Adding…'
                                        : 'Use this course'}
                                </Button>
                                {course && (
                                    <Button
                                        variant="outline-dark"
                                        size="sm"
                                        disabled={unlistedSubmitting}
                                        onClick={returnToCourse}
                                    >
                                        Cancel
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <Form.Group className="mb-3" controlId="ca-enrollment">
                <Form.Label>
                    Maximum course enrollment{' '}
                    <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                    type="number"
                    min="0"
                    placeholder="e.g., 35"
                    value={value.enrollment}
                    onChange={(e) => onChange({ enrollment: e.target.value })}
                    required
                    isInvalid={hasError('ca-enrollment')}
                    aria-invalid={hasError('ca-enrollment')}
                    aria-describedby={
                        hasError('ca-enrollment')
                            ? 'ca-enrollment-help ca-enrollment-error'
                            : 'ca-enrollment-help'
                    }
                />
                {/* The cap, not current headcount or a projection: this
                    number drives the student-to-LA ratio (#436). */}
                <Form.Text id="ca-enrollment-help" className="text-muted">
                    Enter the section&apos;s enrollment cap, not the number of
                    seats currently filled.
                </Form.Text>
                {hasError('ca-enrollment') && (
                    <Form.Control.Feedback
                        type="invalid"
                        id="ca-enrollment-error"
                        role="alert"
                    >
                        This field is required.
                    </Form.Control.Feedback>
                )}
            </Form.Group>

            <Row className={course?.section || course?.crn ? 'mb-1' : 'mb-3'}>
                <Col md={6}>
                    <Form.Group controlId="ca-crn">
                        <Form.Label>
                            CRN <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            inputMode="numeric"
                            placeholder="e.g., 12345"
                            value={value.crn}
                            onChange={(e) => onChange({ crn: e.target.value })}
                            readOnly={!!course?.crn}
                            className={course?.crn ? 'bg-body-secondary' : ''}
                            required
                            isInvalid={hasError('ca-crn')}
                            aria-invalid={hasError('ca-crn')}
                            aria-describedby={
                                hasError('ca-crn')
                                    ? 'ca-crn-error'
                                    : course?.crn
                                      ? 'ca-section-hint'
                                      : undefined
                            }
                        />
                        {crnChecking && (
                            <Form.Text className="d-flex align-items-center gap-2">
                                <Spinner animation="border" size="sm" />
                                Checking CRN…
                            </Form.Text>
                        )}
                        {hasError('ca-crn') && (
                            <Form.Control.Feedback
                                type="invalid"
                                id="ca-crn-error"
                                role="alert"
                            >
                                {!CRN_PATTERN.test(value.crn ?? '')
                                    ? 'Enter the 5-digit CRN.'
                                    : crnChecking
                                      ? 'Still checking this CRN — please wait.'
                                      : 'This CRN already has a support request.'}
                            </Form.Control.Feedback>
                        )}
                        {!crnChecking && crnCheckResult?.exists && (
                            <Alert
                                variant={ownDraftMatch ? 'info' : 'warning'}
                                // The own-draft note informs and blocks
                                // nothing, so it must not interrupt.
                                role={ownDraftMatch ? 'status' : 'alert'}
                                className="mt-2 mb-0 py-2"
                            >
                                {ownDraftMatch ? (
                                    <>
                                        You have another saved draft for CRN{' '}
                                        {value.crn} this term. Only one request
                                        per CRN can be submitted. You can finish
                                        this one, or open the other draft to
                                        continue it or delete it.{' '}
                                        <Button
                                            variant="link"
                                            className="p-0 align-baseline"
                                            onClick={() =>
                                                navigate(
                                                    `/applications/${crnCheckResult.id}`
                                                )
                                            }
                                        >
                                            Open the other draft
                                        </Button>
                                    </>
                                ) : crnCheckResult.own ? (
                                    <>
                                        You already have a request for CRN{' '}
                                        {value.crn} this term.{' '}
                                        <Button
                                            variant="link"
                                            className="p-0 align-baseline"
                                            onClick={() =>
                                                navigate(
                                                    `/applications/${crnCheckResult.id}`
                                                )
                                            }
                                        >
                                            Open it
                                        </Button>
                                    </>
                                ) : crnCheckResult.holder ? (
                                    <CrnHolderNotice
                                        holder={crnCheckResult.holder}
                                        crn={value.crn}
                                        programKeys={rankedProgramKeys}
                                    />
                                ) : (
                                    <>
                                        There is already a request for CRN{' '}
                                        {value.crn} this term.
                                    </>
                                )}
                            </Alert>
                        )}
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group controlId="ca-section-number">
                        <Form.Label>
                            Section # <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="e.g., 001"
                            value={value.sectionNumber}
                            onChange={(e) =>
                                onChange({ sectionNumber: e.target.value })
                            }
                            readOnly={!!course?.section}
                            className={
                                course?.section ? 'bg-body-secondary' : ''
                            }
                            aria-describedby={
                                hasError('ca-section-number')
                                    ? 'ca-section-number-error'
                                    : course?.section
                                      ? 'ca-section-hint'
                                      : undefined
                            }
                            required
                            isInvalid={hasError('ca-section-number')}
                            aria-invalid={hasError('ca-section-number')}
                        />
                        {hasError('ca-section-number') && (
                            <Form.Control.Feedback
                                type="invalid"
                                id="ca-section-number-error"
                                role="alert"
                            >
                                This field is required.
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>
                </Col>
            </Row>
            {(course?.section || course?.crn) && (
                <Form.Text id="ca-section-hint" className="d-block mb-3">
                    Set by the section you picked. To use another section,
                    choose Change course.
                </Form.Text>
            )}

            <div className="mb-3">
                <PartOfTermField
                    value={value}
                    onChange={onChange}
                    partsOfTerm={partsOfTerm}
                    refusal={partOfTermRefusal}
                    hasError={hasError}
                />
            </div>

            <Form.Group className="mb-3">
                <Form.Label>
                    Are you the instructor of record?{' '}
                    <span className="text-danger">*</span>
                </Form.Label>
                <div>
                    <Form.Check
                        inline
                        type="radio"
                        id="ca-ior-yes"
                        name="ca-instructor-of-record"
                        label="Yes"
                        checked={!isSubmittedOnBehalf}
                        onChange={() => onIsSubmittedOnBehalfChange(false)}
                    />
                    <Form.Check
                        inline
                        type="radio"
                        id="ca-ior-no"
                        name="ca-instructor-of-record"
                        label="No"
                        checked={isSubmittedOnBehalf}
                        onChange={() => onIsSubmittedOnBehalfChange(true)}
                    />
                </div>
            </Form.Group>

            {isSubmittedOnBehalf && (
                <Row className="mb-3">
                    <Col md={6}>
                        <Form.Group controlId="ca-ior-name">
                            <Form.Label>Instructor Name (if known)</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Instructor full name"
                                value={value.instructorOfRecordName}
                                onChange={(e) =>
                                    onChange({
                                        instructorOfRecordName: e.target.value,
                                    })
                                }
                                aria-describedby="ca-ior-hint"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group controlId="ca-ior-email">
                            <Form.Label>Instructor Email (if known)</Form.Label>
                            <Form.Control
                                type="email"
                                placeholder="instructor@msudenver.edu"
                                value={value.instructorOfRecordEmail}
                                onChange={(e) =>
                                    onChange({
                                        instructorOfRecordEmail: e.target.value,
                                    })
                                }
                                isInvalid={hasError('ca-ior-email')}
                                aria-invalid={hasError('ca-ior-email')}
                                aria-describedby={
                                    hasError('ca-ior-email')
                                        ? 'ca-ior-email-error ca-ior-hint'
                                        : 'ca-ior-hint'
                                }
                            />
                            {hasError('ca-ior-email') && (
                                <Form.Control.Feedback
                                    type="invalid"
                                    id="ca-ior-email-error"
                                    role="alert"
                                >
                                    Enter a valid email address.
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Col>
                    <Col xs={12}>
                        <Form.Text id="ca-ior-hint">
                            Optional for now, but LA can&apos;t award support
                            until an instructor is named.
                        </Form.Text>
                    </Col>
                </Row>
            )}

            <div className="mb-3">
                <SelectWithOther
                    id="ca-modality"
                    label="Course Modality"
                    placeholder="Select modality..."
                    options={MODALITY_OPTIONS}
                    otherValue="other"
                    value={value.modality}
                    onSelect={(modality) =>
                        onChange({
                            modality,
                            // Clear stale free text once modality is no
                            // longer 'other', same as Section4's
                            // toggleSupportUseCase does for its checkboxes.
                            ...(modality !== 'other' && { otherModality: '' }),
                        })
                    }
                    otherId="ca-other-modality"
                    otherText={value.otherModality}
                    otherLabel="Describe the other modality"
                    otherPlaceholder="e.g. flipped classroom"
                    onOtherChange={(otherModality) =>
                        onChange({ otherModality })
                    }
                    hasError={hasError}
                />
            </div>

            <MeetingFields
                meetings={value.meetings}
                onChange={(meetings) => onChange({ meetings })}
                hasError={hasError}
            />
        </div>
    );
}

export default CreateApplicationSection1;
